/**
 * Help Centre contract (api.core `api/v1/HelpCentre`) — mirrors the backend's
 * `docs/help-centre-api.md` (backend repo, branch feat/help-centre).
 *
 * The reader routes are permission-filtered SERVER-SIDE: a guide the caller may not see is absent
 * from the tree and search, and its slug answers the same 404 as a slug that never existed. The
 * frontend therefore never filters guides itself — it renders what it is given.
 */

export type HelpCalloutType = 'prerequisite' | 'billed' | 'irreversible' | 'tip';
export const HELP_CALLOUT_TYPES: HelpCalloutType[] = ['prerequisite', 'billed', 'irreversible', 'tip'];

export interface IHelpScreenshot {
	url: string;
	/** Required with a screenshot — an image without alt text is one a screen reader skips. */
	alt: string;
	width?: number | null;
	height?: number | null;
}

export interface IHelpStep {
	title: string;
	bodyMd: string;
	screenshot?: IHelpScreenshot | null;
	/** `data-help-anchor` the P3 guided tour targets. Stored now so content does not need a rewrite. */
	anchor?: string | null;
	/** The "You'll see" line: the expected result, in the app's own words. */
	expect?: string | null;
	/** e.g. "Super Admin only" — a step only some readers can perform, inside an otherwise visible guide. */
	roleNote?: string | null;
}

export interface IHelpCallout {
	type: HelpCalloutType;
	text: string;
}

export type HelpArticleKind = 'Guide' | 'Concept' | (string & {});

export interface IHelpArticleSummary {
	slug: string;
	title: string;
	summary: string;
	kind: HelpArticleKind;
	moduleKey: string;
	moduleTitle?: string;
	/** The article's own journey, else its module's. Drives the home page grouping. */
	journeyKey?: string | null;
	estMinutes: number | null;
	/** Not on the tree/context summary today; shown only when present. */
	stepCount: number | null;
	updatedAt: string | null;
}

export interface IHelpModuleNode {
	key: string;
	title: string;
	/** Menu section — not sent by the server; '' unless known locally. */
	section: string;
	journeyKey: string | null;
	routeUrl: string | null;
	parentKey: string | null;
	articles: IHelpArticleSummary[];
}

export interface IHelpTree {
	totalCount: number;
	modules: IHelpModuleNode[];
}

export interface IHelpArticle {
	slug: string;
	moduleKey: string;
	moduleTitle: string;
	kind: HelpArticleKind;
	title: string;
	summary: string;
	steps: IHelpStep[];
	prerequisites: string[];
	callouts: IHelpCallout[];
	estMinutes: number | null;
	updatedAt: string | null;
}

export interface IHelpCapabilities {
	enabled: boolean;
	/** `fts` (ranked, stemmed) on Postgres, `basic` (substring) elsewhere. */
	search: 'fts' | 'basic' | (string & {});
}

export interface IHelpHighlight {
	/** UTF-16 offsets into `snippet` — JavaScript string indexing. */
	start: number;
	length: number;
}

export interface IHelpSearchHit {
	slug: string;
	title: string;
	summary: string;
	moduleKey: string;
	moduleTitle: string;
	/** PLAIN TEXT, never HTML. Matches are marked by `highlights`, not by markup. */
	snippet: string;
	highlights: IHelpHighlight[];
}

export interface IHelpSearchResult {
	items: IHelpSearchHit[];
	totalCount: number;
	page: number;
	pageSize: number;
}

export interface IHelpModuleContext {
	module: { key: string; title: string; routeUrl: string | null } | null;
	articles: IHelpArticleSummary[];
}

// ── Editor ────────────────────────────────────────────────────

/** One `data.errors[]` entry of a 400 — `field` is a path such as `steps[1].bodyMd` or `callouts[0].text`. */
export interface IHelpFieldError {
	field: string;
	message: string;
}

/** Derived for display: never published, live, or live and since withdrawn. */
export type HelpArticleState = 'Draft' | 'Published' | 'Unpublished';
export type HelpVersionState = 'Draft' | 'Published' | 'Superseded' | 'Withdrawn' | (string & {});

/** The `state` filter values the server accepts. */
export type HelpStateFilter = 'published' | 'unpublished' | 'draft';

export interface IHelpAdminArticleRow {
	id: number;
	slug: string;
	title: string;
	moduleKey: string;
	moduleTitle: string;
	kind: HelpArticleKind;
	state: HelpArticleState;
	hasDraft: boolean;
	updatedAt: string | null;
}

export interface IHelpAdminArticleList {
	items: IHelpAdminArticleRow[];
	totalCount: number;
}

/** The editable content of a version. */
export interface IHelpContent {
	title: string;
	summary: string;
	steps: IHelpStep[];
	prerequisites: string[];
	callouts: IHelpCallout[];
	estMinutes: number | null;
	/** Extra OR-gate on top of the module's — empty means "everyone who can open the module". */
	extraRequiredActions: string[];
	/** OR-set of role names — empty means "any role". */
	requiredRoles: string[];
}

/**
 * Body of `PUT admin/articles/{id}/draft` — a FULL REPLACE. Every field except `changeNote` is
 * required, so they are all required here too: an omitted field is a 400, and a type that let a
 * caller omit one would hide that until runtime.
 */
export interface IHelpDraftBody extends Omit<IHelpContent, 'estMinutes'> {
	estMinutes: number;
	/** Concurrency token. A mismatch answers 409 `STALE_ROW_VERSION`. */
	rowVersion: number;
	changeNote?: string;
}

export interface IHelpAdminArticle {
	id: number;
	slug: string;
	moduleKey: string;
	moduleTitle: string;
	kind: HelpArticleKind;
	state: HelpArticleState;
	rowVersion: number;
	/** The pending draft's version id — what publish must name. Null when the live version is current. */
	draftVersionId: number | null;
	/** The draft if there is one, else the live version, else empty. */
	content: IHelpContent;
	hasDraft: boolean;
	isPublished: boolean;
	publishedAt: string | null;
	updatedAt: string | null;
}

export interface IHelpVersion {
	id: number;
	versionNumber: number;
	state: HelpVersionState;
	title: string;
	changeNote: string | null;
	createdAt: string | null;
	publishedAt: string | null;
}

export interface IHelpVisibilityPreview {
	roles: string[];
	/** Names that matched no role — listed so a typo isn't read as "sees nothing". */
	unknownRoles: string[];
	tree: IHelpTree;
}

/** One module the sync button posts — built from `menuData`, never hand-written. */
export interface IHelpModuleSync {
	key: string;
	title: string;
	parentKey: string | null;
	journeyKey: string;
	routeUrl: string;
	requiredActions: string[];
	superAdminOnly: boolean;
	observerAdminOnly: boolean;
	isGeneral: boolean;
	/**
	 * Deliberately NEVER sent by the sync: omitted, a new module starts active and an existing one
	 * keeps its state — so a re-sync cannot re-activate a module an editor switched off.
	 */
	isActive?: never;
	sortOrder: number;
}
